<template>
  <!-- data-specificy-hack is for the wrapping classes to get higher specificity than the default pill class.
   Using this, they can override e.g. color/bordercolor without relying on the CSS-files loading in the right order. -->
  <component
    v-bind="$attrs"
    :is="tag"
    data-specificity-hack
    :class="{
      pill: true,
      'pill--circle-sides': circleSides,
      'pill--clickable': clickable || hoverMode,
      'pill--counter': counter,
      'pill--not-fixed': !fixed,
      'pill--inversed': inversed,
      'pill--outlined': _outlined,
      'pill--striped': striped,

      'pill--small': size === 'small',
      'pill--semi-small': size === 'semi-small',
      'pill--medium': size === 'medium',
      'pill--semi-large': size === 'semi-large',
      'pill--large': size === 'large',
      'pill--extra-large': size === 'extra-large',
      'pill--tiny': size === 'tiny',

      'pill--add-mode': hoverMode === 'add',
      'pill--clear-mode': hoverMode === 'clear',
      'pill--remove-mode': hoverMode === 'remove',
    }"
    :style="style"
  >
    <span
      v-if="hoverMode"
      :class="{
        'plus-or-cross-icon': true,
        'pill__plus-or-cross-icon': true,
        'plus-or-cross-icon--small        pill__plus-or-cross-icon--small': size === 'small' || size === 'semi-small',
        'plus-or-cross-icon--medium       pill__plus-or-cross-icon--medium': size === 'medium' || size === 'semi-large',
        'plus-or-cross-icon--large        pill__plus-or-cross-icon--large': size === 'large' || size === 'extra-large',
        'plus-or-cross-icon--45-deg-turn  pill__plus-or-cross-icon--45-deg-turn': hoverMode === 'clear',
      }"
      v-html="hoverMode === 'remove' ? icons.singles.trashCanIcon : icons.singles.addIcon"
    ></span>

    <slot name="pre"></slot>

    <!-- Hidden by default; revealed (with the pill chrome stripped) by compact
         table density to render the pill as a small colored dot + text. -->
    <span class="pill__dot" aria-hidden="true"></span>

    <span
      :class="{
        pill__text: true,
      }"
    >
      <slot></slot>
    </span>

    <slot name="post"></slot>
  </component>
</template>

<script>
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

import { calculateColorBrightness } from '@lib/colorManagement';

// On an outlined pill the bands land on this lightness whatever the hue, so none of them matches
// its own near-white background. Low enough to read, high enough that the label still leads.
const STRIPE_LIGHTNESS = 222;
// On a solid pill the background is the colour, so the bands are just a fraction darker than it.
const STRIPE_DARKEN = 0.85;
const STRIPE_ON_DARK = 'rgba(255, 255, 255, 0.2)';

// Icons
// singles
import addIcon from '@icons/other/add.svg';
import trashCanIcon from '@icons/other/trash-can-icon.svg';

export default {
  name: 'fs-pill',

  props: {
    circleSides: {
      type: Boolean,
      default: false,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    // String: The color of the pill, styled based on type (or, should be based on solid/outlined - see below)
    // Object: Style properties, fully managed (basically passed directly into the style-attribute)
    color: {
      type: [Object, String],
    },
    counter: {
      type: Boolean,
      default: false,
    },
    fixed: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    striped: {
      type: Boolean,
      default: false,
    },
    outlinedColored: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      validator: (size) =>
        ['tiny', 'small', 'semi-small', 'medium', 'semi-large', 'large', 'extra-large'].includes(size),
      default: 'medium',
    },
    // FUTURE: If we need to support router-link, then look at fs-button on how to implement it
    tag: {
      type: String,
      default: 'span',
    },
    hoverMode: { type: String, validator: (mode) => ['add', 'clear', 'remove'].includes(mode) },
  },

  data() {
    return {
      icons: {
        singles: {
          addIcon,
          trashCanIcon,
        },
      },
    };
  },

  computed: {
    colorIsBright() {
      if (this.rainbow) return true;

      if (this.color === 'transparent') return true;

      return calculateColorBrightness(this.color) > 205;
    },

    colorIsReallyBright() {
      if (this.rainbow) return true;

      if (this.color === 'transparent') return true;

      return calculateColorBrightness(this.color) > 240;
    },

    colorIsObject() {
      return !isString(this.color) && isObject(this.color);
    },

    style() {
      if (this.rainbow) {
        return {
          borderWidth: '0',
          color: '#fff',
          backgroundColor: 'rgb(224,89,100)',
          background: 'linear-gradient(90deg, rgba(224,89,100,1) 0%, rgba(124,199,106,1) 50%, rgba(91,107,199,1) 100%)',
        };
      }

      if (!this.color) return {};

      if (this.colorIsObject) return this.color;

      const style = {};
      // Expose the resolved color as a CSS var so the dot variant (compact table
      // density) can tint its dot regardless of solid/outlined styling.
      style['--fs-pill-color'] = this.color;
      // Note! This is based on the outlined prop - NOT the _outlined computed value (which takes "inverse" scenarios, solid background too bright to have white text, into considerations)
      style[this.outlined || this.outlinedColored ? 'borderColor' : 'backgroundColor'] = this.color;

      if (this.outlinedColored) style['color'] = this.color;

      if (this.striped) {
        if (this.outlined) style.backgroundColor = this.convertToRgba(this.color, 0.06);

        const band = this.stripeColor;
        style.backgroundImage = `repeating-linear-gradient(-45deg, ${band} 0, ${band} 5px, rgba(0, 0, 0, 0) 5px, rgba(0, 0, 0, 0) 10px)`;
      }

      if (this.color === 'transparent') {
        style.backgroundColor = 'transparent';
        style.color = 'rgb(53,73,94)';
        style.borderColor = 'rgb(53,73,94)';
      }

      return style;
    },

    /**
     * The colour of a striped pill's bands. The two pill shapes need different arithmetic because
     * they have different backgrounds behind the bands.
     *
     * A solid pill's background is the colour itself, so the band only has to step away from it —
     * a fixed fraction darker always shows, and white still reads on a dark colour. An outlined
     * pill's background is that same colour at 6%, which is near white whatever the hue, so a step
     * relative to the colour means nothing: a pale season would stripe white on white. There the
     * band has to land on a lightness of its own.
     */
    stripeColor() {
      const parts = isString(this.color) ? this.color.match(/\d+/g) : null;
      if (!parts || parts.length < 3) return STRIPE_ON_DARK;

      const rgb = parts.slice(0, 3).map(Number);

      if (!this.outlined && !this.outlinedColored) {
        if (!this.colorIsBright) return STRIPE_ON_DARK;
        return `rgb(${rgb.map((v) => Math.round(v * STRIPE_DARKEN)).join(', ')})`;
      }

      const lightness = calculateColorBrightness(`rgb(${rgb.join(',')})`);
      if (!lightness) return STRIPE_ON_DARK;

      const shifted =
        lightness > STRIPE_LIGHTNESS
          ? rgb.map((v) => Math.round(v * (STRIPE_LIGHTNESS / lightness)))
          : rgb.map((v) => Math.round(v + (255 - v) * (1 - lightness / STRIPE_LIGHTNESS)));

      return `rgb(${shifted.join(', ')})`;
    },

    inversed() {
      return !this._outlined && this.color && !this.colorIsObject && this.colorIsBright;
    },

    _outlined() {
      return this.outlined || this.outlinedColored || (this.color && !this.colorIsObject && this.colorIsReallyBright);
    },

    rainbow() {
      return this.color === 'rainbow';
    },
  },

  methods: {
    convertToRgba(rgbString, alpha = 1.0) {
      // Use regex to extract the r, g, b values from the string
      const rgbValues = rgbString.match(/\d+/g);

      if (rgbValues && rgbValues.length === 3) {
        return `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, ${alpha})`;
      } else {
        // Return the original string if it doesn't match the format
        return rgbString;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@styles/text';
@import '@styles/variables';

// Plus or Cross icon ---------------------------------------------------------
.plus-or-cross-icon {
  display: flex;
  justify-content: center;
  align-items: center;

  &--tiny {
    width: 1rem;
    height: 1rem;
  }
  &--small {
    width: 1rem;
    height: 1rem;
  }
  &--medium {
    width: 1.2rem;
    height: 1.2rem;
  }
  &--large {
    width: 1.4rem;
    height: 1.4rem;
  }

  &--45-deg-turn {
    transform: rotate(45deg);
  }

  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

// TODO: REVIEW/REFACTOR Should we perhaps add an "icon"-version so that we can have
// New supplier page -> .footwear-type / __icon-container be a type of pill?
// They sure look like pills with icons + labels. It would be easy to only do the
// icon part - but the labels are quite critical (also since the container is where
// the hover lies). OR, are they simply looking very similar but are not really pills?
.pill {
  max-width: 100%;
  flex-shrink: 0;
  min-width: 7.4rem;
  height: 2rem;
  padding: 0 0.6rem;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  // Defaults to solid style, rather than outlined
  border: 1px transparent solid;
  border-radius: $br-medium;
  position: relative;
  color: $c-light;
  // Default size (aka medium)
  @include caption--small;
  fill: $c-light;
  background-color: $c-dark;
  user-select: none;

  &--add-mode,
  &--clear-mode,
  &--remove-mode {
    cursor: pointer;
  }

  // Small colored dot, shown only when an ancestor opts in (compact table
  // density) by setting `display` + stripping the pill's box. Tinted from the
  // resolved pill color so it works for both solid and outlined pills.
  &__dot {
    display: none;
    flex-shrink: 0;
    width: 0.7rem;
    height: 0.7rem;
    margin-right: 0.5rem;
    border-radius: 50%;
    background: var(--fs-pill-color, currentColor);
  }

  &__text {
    width: auto;
    max-width: 100%;
    padding: 2px 0; // Because of overflow: hidden, g's get cut off at the bottom - so we add extra padding to accomodate for that.
    display: block;
    justify-content: flex-start;
    align-content: center;
    align-items: center;
    position: relative;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1;
    text-align: center;
    opacity: 1;
    transition:
      color 0.1s ease,
      fill 0.1s ease,
      opacity 0.05s ease-in-out;
  }

  // The origin badge stays put while the pill's contents fade out for the
  // hover icon — it marks where the tag comes from, not what clicking will do.
  &--add-mode:hover > :not(.plus-or-cross-icon, .pill-origin-badge),
  &--clear-mode:hover > :not(.plus-or-cross-icon, .pill-origin-badge),
  &--remove-mode:hover > :not(.plus-or-cross-icon, .pill-origin-badge) {
    opacity: 0;
  }

  &__plus-or-cross-icon {
    position: absolute;
    transform: translateY(-1px); // compensates text top 1px padding
    opacity: 0;
    transition: opacity 0.05s ease-in-out;

    &--small {
      inset: calc(50% - 0.5rem) auto auto calc(50% - 0.5rem);
    }
    &--medium {
      inset: calc(50% - 0.6rem) auto auto calc(50% - 0.6rem);
    }
    &--large {
      inset: calc(50% - 0.7rem) auto auto calc(50% - 0.7rem);
    }

    &--45-deg-turn {
      transform: translateY(0) rotate(45deg);
    }
  }

  &:hover &__plus-or-cross-icon {
    opacity: 1;
  }

  &--clear-mode:hover &__text {
    color: rgba(180, 180, 180, 0);
  }

  &--clear-mode:hover &__cross {
    :deep(svg) {
      fill: rgba(255, 255, 255, 1);
    }
  }

  &--clear-mode.pill--outlined:hover &__cross {
    :deep(svg) {
      fill: rgba($c-dark, 1);
    }
  }

  // Size modifiers
  // ==============================================

  &--extra-large {
    height: 3rem;
    padding: 0 1.6rem;
    border-radius: $br-heavy;
    border-width: 0.2rem;
    // min-width: TBD;
    text-transform: uppercase;
  }

  &--large {
    height: 2.6rem;
    padding: 0 1.2rem;
    min-width: 12rem;

    &.pill--circle-sides {
      padding: 0 1.4rem;
    }
  }

  &--semi-large {
    height: 2.4rem;
    // padding: TBD;
    // min-width: TBD;

    &.pill--circle-sides {
      padding: 0 1.2rem;
    }
  }

  &--circle-sides {
    padding: 0 0.8rem; // REFACTOR - should not be here? Width/padding (width/sizing) should be handled elsewhere
  }

  &--semi-small {
    height: 1.8rem;
    padding: 0 1px;
    font-size: 1.1rem;
    min-width: 6.8rem;
  }

  &--small {
    height: 1.5rem;
    padding: 0 3px;
    font-size: 1rem;
    // min-width: TBD;
  }

  &--tiny {
    height: 1.5rem;
    padding: 0 3px;
    font-size: 1rem;
    min-width: unset;
  }

  // Other modifiers
  // ==============================================

  &--inversed {
    fill: $c-dark;
    color: $c-dark;
  }

  &--outlined {
    fill: $c-dark;
    color: $c-dark;
    background-color: $c-bg-light;
    border-color: $c-dark;
  }

  &--clickable {
    cursor: pointer;
  }

  &--circle-sides {
    border-radius: $br-round-corner;
  }

  &--not-fixed {
    min-width: 0;
  }

  // The counter only exists in below sizes
  &--counter {
    font-size: 1rem;
    font-weight: $bold-lato;
    padding-right: 0.4rem;
    padding-left: 0.3rem; // TODO: REVIEW padding, should it be 0.25rem on both sides as default?;
    min-width: 0;

    &.pill--small {
      padding-right: 2px;
      padding-left: 2px;
    }
  }
}
</style>
